import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Landmark, Loader2, Upload, Copy, CheckCircle2, Clock, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export const periodPrice = (price, per) => {
  const gross = price * per.months;
  const total = Math.round(gross * (100 - per.discount)) / 100;
  return { gross, total, monthly: Math.round((total / per.months) * 100) / 100, saving: Math.round((gross - total) * 100) / 100 };
};

export const PeriodToggle = ({ periods, value, onChange }) => (
  <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm" data-testid="billing-period-toggle">
    {periods.map((p) => (
      <button key={p.months} onClick={() => onChange(p)} data-testid={`billing-period-${p.months}`}
        className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${value?.months === p.months ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
        {p.label}{p.discount ? <span className={`ms-1 text-[11px] ${value?.months === p.months ? "text-blue-100" : "text-emerald-600"}`}>−{p.discount}%</span> : null}
      </button>
    ))}
  </div>
);

const Row = ({ label, value, testId }) => (
  <div className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="flex items-center gap-1.5 font-semibold" dir="ltr" data-testid={testId}>{value || "—"}
      {value && <button onClick={() => { navigator.clipboard?.writeText(value); toast.success("تم النسخ"); }}><Copy className="h-3.5 w-3.5 text-slate-400" /></button>}</span>
  </div>
);

export const BankTransferDialog = ({ plan, period, bank, onClose, onDone }) => {
  const inp = useRef(null);
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const pr = periodPrice(plan.price, period);
  const send = async () => {
    if (!file) { toast.error("أرفق إيصال التحويل"); return; }
    setBusy(true);
    try {
      const up = await api.uploadFile(file);
      await api.billingBankRequest({ plan_id: plan.id, months: period.months, receipt_id: up.attachment_id, note });
      toast.success("تم إرسال طلبك — سيُفعّل الاشتراك بعد مراجعة الإيصال"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="max-w-md" data-testid="bank-transfer-dialog">
        <DialogHeader className="text-right"><DialogTitle className="font-heading">الدفع بالتحويل البنكي</DialogTitle>
          <DialogDescription>باقة {plan.name_ar} — {period.label}</DialogDescription></DialogHeader>
        <div className="rounded-xl bg-blue-50 p-3 text-center"><p className="text-xs text-blue-700">المبلغ المطلوب</p>
          <p className="font-heading text-3xl font-extrabold text-blue-900" data-testid="bank-amount">{pr.total} <span className="text-base">{plan.currency}</span></p>
          {pr.saving > 0 && <p className="text-xs font-semibold text-emerald-700">وفّرت {pr.saving} {plan.currency}</p>}</div>
        <div className="space-y-1.5">
          <Row label="البنك" value={bank.bank_name} testId="bank-name" />
          <Row label="الآيبان" value={bank.iban} testId="bank-iban" />
          <Row label="المستفيد" value={bank.beneficiary} testId="bank-beneficiary" />
          {bank.transfer_note && <p className="text-xs text-slate-500">{bank.transfer_note}</p>}
          {!bank.iban && <p className="text-xs text-amber-700">لم تُضف بيانات الحساب البنكي بعد — تواصل معنا عبر واتساب.</p>}
        </div>
        <input ref={inp} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="bank-receipt-input" />
        <Button variant="outline" onClick={() => inp.current?.click()} className="gap-1.5" data-testid="bank-receipt-pick"><Upload className="h-4 w-4" /> {file ? file.name : "إرفاق إيصال التحويل"}</Button>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="ملاحظة (اختياري)" />
        <Button onClick={send} disabled={busy} className="bg-blue-600" data-testid="bank-submit">{busy && <Loader2 className="me-1 h-4 w-4 animate-spin" />}إرسال للتفعيل</Button>
      </DialogContent>
    </Dialog>
  );
};

const ST = { pending: [Clock, "قيد المراجعة", "text-amber-700 bg-amber-50"], approved: [CheckCircle2, "تم التفعيل", "text-emerald-700 bg-emerald-50"], rejected: [XCircle, "مرفوض", "text-red-700 bg-red-50"] };

export const MyBankRequests = ({ tick }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.billingMyRequests().then((r) => setItems(r.items || [])).catch(() => {}); }, [tick]);
  if (!items.length) return null;
  return (
    <div className="mx-auto mb-8 max-w-2xl space-y-1.5" data-testid="my-bank-requests">
      {items.slice(0, 3).map((r) => { const [I, l, c] = ST[r.status] || ST.pending; return (
        <div key={r.id} className={`flex flex-wrap items-center gap-2 rounded-xl px-3 py-2 text-sm ${c}`} data-testid={`my-bank-req-${r.id}`}>
          <Landmark className="h-4 w-4" /> تحويل بنكي — {r.plan_name} ({r.months} شهر) · {r.amount} ر.س
          <span className="ms-auto flex items-center gap-1 font-bold"><I className="h-4 w-4" /> {l}{r.reject_reason ? `: ${r.reject_reason}` : ""}</span>
        </div>); })}
    </div>
  );
};
